import { z } from 'zod'

import { Pagination } from 'app/types/app/request'
import { Site } from 'app/types/park/site'
import { ModelRaspberry } from 'app/types/shared/machine'

/** Position of the microwave unit in the microwave machine. */
export const PositionMicrowave = z.enum(['1', '2', '3'])
export type PositionMicrowave = z.infer<typeof PositionMicrowave>

/** Unity of a microwave inside the microwave machine. */
const MicrowaveUnit = z.object({
    /** ID of the microwave unit. */
    id: z.number().positive(),

    /** Position of the microwave unit in the microwave machine. */
    position: PositionMicrowave,

    /** State of the microwave unit. */
    is_enabled: z.boolean(),

    /** Serial number of the microwave unit. */
    serial_number: z.string().min(1),

    /** Brand of the microwave unit. */
    brand: z.string().min(1),

    /** Model of the microwave unit. */
    model: z.string().min(1),

    /** Power of the microwave unit. */
    power: z.number().positive(),

    /** Purchase invoice of the microwave unit. */
    purchase_invoice: z.string().url().nullable(),
})

export type MicrowaveUnit = z.infer<typeof MicrowaveUnit>

/** The microwave machine that contains multiple microwave units. */
export const Microwave = z.object({
    /** ID of the microwave machine. */
    id: z.number().positive(),

    /** Name of the microwave machine. */
    name: z.string().min(1),

    /** IP address of the microwave machine for the terminal. */
    ip_address: z.string().ip({ version: 'v4' }),

    /** Model of Raspberry inside the microwave machine. */
    model_raspberry: ModelRaspberry,

    /** Serial number of the microwave machine. */
    serial_number: z.string().min(1),

    /** Serial number of the screen inside the microwave machine. */
    serial_number_screen: z.string().min(1),

    /** Serial number of the Raspberry inside the microwave machine. */
    serial_number_raspberry: z.string().min(1),

    /** Array of microwave units inside the microwave machine (1 to 3). */
    microwave_units: MicrowaveUnit.array().min(1).max(3),

    /** Site of the microwave. */
    site: Site,
})

export type Microwave = z.infer<typeof Microwave>

const MicrowaveUnitPayload = MicrowaveUnit.extend({
    id: MicrowaveUnit.shape.id.optional(),
    is_enabled: MicrowaveUnit.shape.is_enabled.optional(),
    power: MicrowaveUnit.shape.power.optional(),
    position: PositionMicrowave,
    purchase_invoice: z.instanceof(File).optional(),
})

export type MicrowaveUnitPayload = z.infer<typeof MicrowaveUnitPayload>

/** The payload to create or update a unit of microwaves. */
export const MicrowavePayload = Microwave.extend({
    id: z.number().positive().optional(),
    site: Site.shape.id,
    microwave_units: MicrowaveUnitPayload.array().min(1).max(3),
})

export type MicrowavePayload = z.infer<typeof MicrowavePayload>

/** List of microwave units returned by the API call. */
export const MicrowavesRequest = Pagination.extend({
    results: Microwave.array(),
})

export type MicrowavesRequest = z.infer<typeof MicrowavesRequest>

export const MicrowaveUnitPayloadSettings = MicrowaveUnit.pick({
    id: true,
    is_enabled: true,
})

export type MicrowaveUnitPayloadSettings = z.infer<
    typeof MicrowaveUnitPayloadSettings
>
